import { ReactNode } from 'react';
import { Activity, Heart, Moon, Timer, TrendingUp, Zap } from 'lucide-react';
import { DailyStats } from '../types';
import { format } from 'date-fns';

interface StatsCardProps {
  stats: DailyStats;
}

interface StatTileProps {
  icon: ReactNode;
  iconClasses: string;
  label: string;
  value: string;
}

function StatTile({ icon, iconClasses, label, value }: StatTileProps) {
  return (
    <div className="rounded-xl bg-zinc-50 p-3.5 ring-1 ring-zinc-950/5 dark:bg-zinc-800/60 dark:ring-white/5">
      <div className={`flex items-center gap-1.5 text-xs font-medium ${iconClasses}`}>
        {icon}
        {label}
      </div>
      <p className="stat-number mt-1.5">{value}</p>
    </div>
  );
}

export function StatsCard({ stats }: StatsCardProps) {
  const formatDuration = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${hours}h ${minutes}m`;
  };

  const formatDistance = (meters?: number) => {
    if (!meters) return 'N/A';
    const miles = meters / 1609.34;
    return `${miles.toFixed(1)} mi`;
  };

  return (
    <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
      {stats.oura && (
        <div className="card p-6">
          <div className="mb-5 flex items-center justify-between">
            <h2 className="flex items-center gap-2 text-base font-semibold text-zinc-900 dark:text-white">
              <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-violet-600/10 text-violet-600 dark:bg-violet-400/10 dark:text-violet-400">
                <Moon className="h-4 w-4" />
              </span>
              Sleep &amp; Recovery
            </h2>
            <span className="text-xs font-medium text-zinc-400">
              {format(new Date(stats.oura.sleep.timestamp), 'MMM d')}
            </span>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <StatTile
              icon={<Timer className="h-3.5 w-3.5" />}
              iconClasses="text-violet-600 dark:text-violet-400"
              label="Sleep duration"
              value={formatDuration(stats.oura.sleep.duration)}
            />
            <StatTile
              icon={<Moon className="h-3.5 w-3.5" />}
              iconClasses="text-violet-600 dark:text-violet-400"
              label="Sleep quality"
              value={`${stats.oura.sleep.quality}%`}
            />
            <StatTile
              icon={<Activity className="h-3.5 w-3.5" />}
              iconClasses="text-emerald-600 dark:text-emerald-400"
              label="HRV"
              value={`${stats.oura.recovery.hrv}ms`}
            />
            <StatTile
              icon={<Heart className="h-3.5 w-3.5" />}
              iconClasses="text-rose-600 dark:text-rose-400"
              label="Resting HR"
              value={`${stats.oura.recovery.restingHeartRate} bpm`}
            />
          </div>

          {stats.oura.recovery.readinessScore !== undefined && (
            <div className="mt-3 flex items-center justify-between rounded-xl bg-zinc-50 px-4 py-3 ring-1 ring-zinc-950/5 dark:bg-zinc-800/60 dark:ring-white/5">
              <span className="text-sm text-zinc-600 dark:text-zinc-400">Oura readiness score</span>
              <span className="text-sm font-bold tabular-nums text-zinc-900 dark:text-white">
                {stats.oura.recovery.readinessScore}
              </span>
            </div>
          )}
        </div>
      )}

      {stats.strava && (
        <div className="card p-6">
          <div className="mb-5 flex items-center justify-between">
            <h2 className="flex items-center gap-2 text-base font-semibold text-zinc-900 dark:text-white">
              <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-amber-600/10 text-amber-600 dark:bg-amber-400/10 dark:text-amber-400">
                <Activity className="h-4 w-4" />
              </span>
              Recent Activity
            </h2>
            <span className="text-xs font-medium text-zinc-400">Last 7 days</span>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <StatTile
              icon={<Timer className="h-3.5 w-3.5" />}
              iconClasses="text-amber-600 dark:text-amber-400"
              label="Total time"
              value={formatDuration(stats.strava.summary.totalDuration)}
            />
            <StatTile
              icon={<TrendingUp className="h-3.5 w-3.5" />}
              iconClasses="text-amber-600 dark:text-amber-400"
              label="Distance"
              value={formatDistance(stats.strava.summary.totalDistance)}
            />
            {stats.strava.summary.averageHeartRate ? (
              <StatTile
                icon={<Heart className="h-3.5 w-3.5" />}
                iconClasses="text-rose-600 dark:text-rose-400"
                label="Avg heart rate"
                value={`${Math.round(stats.strava.summary.averageHeartRate)} bpm`}
              />
            ) : null}
            {stats.strava.summary.totalCalories ? (
              <StatTile
                icon={<Zap className="h-3.5 w-3.5" />}
                iconClasses="text-amber-600 dark:text-amber-400"
                label="Calories"
                value={`${Math.round(stats.strava.summary.totalCalories)} kcal`}
              />
            ) : null}
          </div>

          {stats.strava.workouts.length > 0 && (
            <div className="mt-4 border-t border-zinc-950/5 pt-4 dark:border-white/5">
              <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-zinc-400">
                Sessions
              </h3>
              <div className="space-y-2">
                {stats.strava.workouts.map((workout) => (
                  <div key={workout.id} className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2 text-zinc-700 dark:text-zinc-300">
                      <Zap className="h-3.5 w-3.5 text-amber-500" />
                      {workout.type}
                    </div>
                    <span className="tabular-nums text-zinc-500 dark:text-zinc-400">
                      {formatDuration(workout.duration)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
